import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, LogIn, Menu, ShoppingBag, X } from 'lucide-react';
import Container from './Container';
import { logoUrl, useSite } from '../../utils/site';
import { useAuth } from '../../hooks/useAuth';
import useScrollSpy from '../../hooks/useScrollSpy';

const NAV_LINKS = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'products', label: 'Products' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'partners', label: 'Our Partners' },
  { id: 'help', label: 'Help' },
  { id: 'contact', label: 'Contact' },
];

export default function Header() {
  const site = useSite();
  const [open, setOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const activeId = useScrollSpy(NAV_LINKS.map((l) => l.id));
  const navigate = useNavigate();
  const location = useLocation();

  // On the one-page route, a plain #id anchor is enough (smooth-scroll is
  // set globally in index.css). From anywhere else (the old /about etc.
  // paths mid-redirect, or 404) there's nothing on the page yet to scroll
  // to, so navigate to "/" first and let the hash-scroll effect in
  // Layout.tsx land on the right section once it mounts.
  const goTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      window.history.replaceState(null, '', `/#${id}`);
    } else {
      navigate(`/#${id}`);
    }
  };

  const linkClasses = (id: string) =>
    `text-sm font-medium transition-colors ${
      activeId === id ? 'text-brand-green-700' : 'text-ink-500 hover:text-brand-green-700'
    }`;

  return (
    <header className="sticky top-0 z-40 border-b border-brand-green-100 bg-surface/95 backdrop-blur">
      <Container className="flex h-16 items-center justify-between gap-4">
        <a href="/#home" onClick={goTo('home')} className="flex items-center gap-3 shrink-0">
          <img src={logoUrl(site)} alt="Sri KalpaGavya logo" className="h-12 w-auto object-contain shrink-0" />
          <span className="text-lg font-bold text-brand-green-900">
            Sri Kalpa<span className="text-brand-gold-600">Gavya</span>
          </span>
        </a>

        <nav className="hidden items-center gap-7 md:flex">
          {NAV_LINKS.map((link) => (
            <a key={link.id} href={`/#${link.id}`} onClick={goTo(link.id)} className={linkClasses(link.id)}>
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <Link
            to="/shop"
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold-500 px-5 py-2 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-gold-600"
          >
            <ShoppingBag className="h-4 w-4" /> Order Online
          </Link>
          <Link
            to={isAuthenticated ? '/dashboard' : '/login'}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-brand-green-700 px-4 py-2 text-sm font-semibold text-brand-green-700 transition-colors hover:bg-brand-green-50"
          >
            {isAuthenticated ? <LayoutDashboard className="h-4 w-4" /> : <LogIn className="h-4 w-4" />}
            {isAuthenticated ? 'Dashboard' : 'Sign In'}
          </Link>
        </div>

        <Link to="/shop" className="ml-auto inline-flex items-center gap-1 rounded-full bg-brand-gold-500 px-3 py-1.5 text-xs font-bold text-white md:hidden">
          <ShoppingBag className="h-3.5 w-3.5" /> Order
        </Link>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md p-2 text-brand-green-800 md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </Container>

      {open && (
        <div className="border-t border-brand-green-100 bg-surface md:hidden">
          <Container className="flex flex-col gap-1 py-3">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={`/#${link.id}`}
                onClick={goTo(link.id)}
                className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                  activeId === link.id ? 'bg-brand-green-50 text-brand-green-700' : 'text-ink-500 hover:bg-brand-green-50'
                }`}
              >
                {link.label}
              </a>
            ))}
            <Link
              to={isAuthenticated ? '/dashboard' : '/login'}
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-brand-green-700 px-4 py-2.5 text-sm font-semibold text-brand-green-700"
            >
              {isAuthenticated ? <LayoutDashboard className="h-4 w-4" /> : <LogIn className="h-4 w-4" />}
              {isAuthenticated ? 'Dashboard' : 'Sign In'}
            </Link>
          </Container>
        </div>
      )}
    </header>
  );
}
